import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

import {forkJoin, Observable} from 'rxjs';
import {endWith} from 'rxjs/operators';
import {PriceCategory} from '~core/models/tixx-select/price-category';
import {PriceReduction} from '~core/models/tixx-select/price-reduction';
import {DefaultNotificationService} from '~core/services';
import {PriceCategoryService} from '~core/services/tixx-select/price-category.service';
import {PriceReductionService} from '~core/services/tixx-select/price-reduction.service';
import {NotificationType} from '~core/enum/notification.enum';
import {TixxSelectTranslationsService} from '~shared/translations/alerts/tixx-select-overview-translations.service';
import {Lottery} from '~core/models';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {EventAdministrationService} from "~features/tixx-select/event-administration.service";

@UntilDestroy()
@Component({
  selector: 'app-assigned-reductions',
  templateUrl: './assigned-reductions.component.html'
})
export class AssignedReductionsComponent implements OnInit, OnDestroy {
  @Input() selectedLottery: Lottery;
  public priceReductionList: Array<PriceReduction>;
  public currentPriceCategory: PriceCategory;
  public assignedReductionsForm: UntypedFormGroup;
  public spinnerName: string = 'editAssignedReductions';
  public i18nHeading: string;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private notificationService: DefaultNotificationService,
    private priceCategoryService: PriceCategoryService,
    private priceReductionService: PriceReductionService,
    public tixxSelectOverviewService: EventAdministrationService,
    public tixxSelectTranslationService: TixxSelectTranslationsService,
    public flTranslations: FormLabelTranslationService,
    public buttonsTranslations: ButtonsTranslations,
    public spinnerService: SpinnerService,
  ) {
    this.priceReductionService.castCurrentPriceReductionList.pipe(untilDestroyed(this))
      .subscribe((data: Array<PriceReduction>) => this.priceReductionList = data);
    this.priceCategoryService.castCurrentPriceCategory.pipe(untilDestroyed(this))
      .subscribe(data => {
        if (data) {
          this.currentPriceCategory = data;
        }
      });
  }

  ngOnInit(): void {
    this.i18nHeading = $localize`:@@tixxSelect.modalTitle.editAssignedReductions:Edit assigned reductions`;
    this.fillForm();
    this.assignCheckedReductions();
    this.assignedReductionReducedAmount();
  }

  ngOnDestroy(): void {
    this.tixxSelectOverviewService.toggleEditAssignedReductionsModal(false);
  }

  get modalStatus(): boolean {
    return this.tixxSelectOverviewService.listOfModals.editAssignedReductionsModalOpened;
  }

  /**
   * Create the lottery form.
   */
  public fillForm(): void {
    this.assignedReductionsForm = this.formBuilder.group({
      priceCategory: this.currentPriceCategory['@id'],
      reduction: '',
      reducedAmount: '',
      currencyCode: 'EUR',
    });
  }

  public formSubmit(): void {
    this.spinnerService.show(this.spinnerName);
    const calls: Array<undefined | Observable<any>> = [];
    for (const priceReduction of this.priceReductionList) {
      const assignedReduction = this.currentPriceCategory.assignedReductions.find(assigned => assigned.reduction.id === priceReduction.id);

      let newAmount: any = (priceReduction?.reducedAmount ? priceReduction.reducedAmount : this.currentPriceCategory.amount) * 100;
      newAmount = Math.trunc(newAmount).toString();

      this.assignedReductionsForm.patchValue({
        reduction: priceReduction['@id'],
        reducedAmount: newAmount,
      });

      if (priceReduction.checked) {
        if (assignedReduction === undefined) {
          calls.push(
            this.priceCategoryService.createAssignedReduction(this.assignedReductionsForm.value)
          );
        } else if (assignedReduction.reducedAmount !== priceReduction.reducedAmount) {
          const val = this.assignedReductionsForm.value;
          delete val.priceCategory;
          delete val.reduction;
          calls.push(
            this.priceCategoryService.editAssignedReduction(val, assignedReduction.id)
          );
        }
      } else {
        if (assignedReduction) {
          calls.push(
            this.priceCategoryService.deleteAssignedReduction(assignedReduction.id).pipe(endWith([assignedReduction.id]))
          );
        }
      }
    }

    if (calls.length === 0) {
      this.spinnerService.hide(this.spinnerName);
      this.tixxSelectOverviewService.toggleEditAssignedReductionsModal(false);
    }

    forkJoin(calls).subscribe((responseReductions: any) => {
      for (const reduction of responseReductions) {
        if (!Array.isArray(reduction)) {
          const reductionsList = this.currentPriceCategory.assignedReductions;
          const existing = reductionsList.some(allReductions => allReductions.id === reduction.id);

          if (existing) {
            const existingReductionIndex = reductionsList.findIndex(allReductions => allReductions.id === reduction.id);
            reductionsList[existingReductionIndex] = reduction;
          } else {
            this.currentPriceCategory.assignedReductions.push(reduction);
          }
        } else {
          const index = this.currentPriceCategory.assignedReductions.map(el => el.id).indexOf(reduction[0]);
          this.currentPriceCategory.assignedReductions.splice(index, 1);
        }
      }

      this.spinnerService.hide(this.spinnerName);
      this.tixxSelectOverviewService.toggleEditAssignedReductionsModal(false);
      this.notificationService.showNotification({
        type: NotificationType.SUCCESS,
        message: this.tixxSelectTranslationService.i18nSuccessUpdateLottery
      });
      this.fillForm();
    });
  }

  /**
   * Combines both array of attributes together to display the data
   */
  public assignCheckedReductions(): void {
    for (const reduction of this.priceReductionList) {
      reduction.checked = this.currentPriceCategory.assignedReductions.some(assignedReductions => assignedReductions.reduction.id === reduction.id);
    }
  }

  public assignedReductionReducedAmount(): any {
    for (const reduction of this.priceReductionList) {
      const assignedReduction = this.currentPriceCategory.assignedReductions.find(assignedReductions => assignedReductions.reduction.id === reduction.id);
      reduction.reducedAmount = assignedReduction?.reducedAmount ? assignedReduction?.reducedAmount : this.currentPriceCategory.amount;
    }
  }
}
